import React, { FC, useEffect, useState } from "react";
import {
  Table,
  Badge,
  Tooltip,
  Select,
  Button,
  Form,
  message,
  Popconfirm,
} from "antd";
import dayjs from "dayjs";
import { issueApis } from "../../../api/Issue";
import Swal from "sweetalert2";
import { ColumnsType, ColumnType } from "antd/es/table";
import { TooltipComponent } from "../../../components/Component";
import { AddAccess } from "../../../components/accessControl/AccessControl";
import { DeleteOutlined } from "@ant-design/icons";

const { Option } = Select;

export interface IssueTagsInterface {
  tagId: number;
  tagName: string;
  issueTagId: number;
}

export interface LinkedIssueData {
  issueId: string;
  issueName: string;
  linkType: string;
  projectTitle: string;
  reporterName: string;
  issueCreatedAt: Date;
  issueStatus: string;
  issueTags: IssueTagsInterface[];
  assigneeName: string;
  issueEndDate: Date | null;
  severity: string;
  issueLinkId: number;
}

interface LinkIssueTableProps {
  projectId: number;
  minimalMode?: boolean;
  issueDetails: any;
  activeTab?: string;
  isFromWorkUI?: boolean;
  onIssueLinked?: () => void;
}

const LinkIssueTable: FC<LinkIssueTableProps> = ({
  projectId,
  issueDetails,
  minimalMode = false,
  activeTab,
  isFromWorkUI = false,
  onIssueLinked,
}) => {
  const [dataSource, setDataSource] = useState<LinkedIssueData[]>([]);
  const [loading, setLoading] = useState(false);
  const [issueOptions, setIssueOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [linkType, setLinkType] = useState("");
  const [selectedIssue, setSelectedIssue] = useState<number | null>(null);
  const [form] = Form.useForm();

  const fetchIssueDropdown = async () => {
    if (activeTab !== "LinkIssue" || isFromWorkUI) return;
    try {
      const response = await issueApis.fetchIssueDropDown(projectId);
      if (response.status && response.data) {
        const allIssues = response.data;

        // Collect IDs of already linked issues
        const linkedIssueIds =
          issueDetails?.issueLinks?.map((link: any) => link.linkedIssueId) ||
          [];

        const filteredOptions = allIssues
          .filter((issue: any) => {
            // Exclude current issue and already linked ones
            return (
              issue.issueId !== issueDetails.issueId &&
              !linkedIssueIds.includes(issue.issueId)
            );
          })
          .map((issue: any) => ({
            label: issue.issueName,
            value: issue.issueId,
          }));

        setIssueOptions(filteredOptions);
      }
    } catch (error) {
      console.error("Issue dropdown fetch failed", error);
      message.error("Failed to load issues");
    }
  };

  const fetchLinkedIssues = async () => {
    setLoading(true);
    try {
      if (!issueDetails?.issueLinks) {
        setDataSource([]);
        return;
      }
      const linkedIssues = issueDetails.issueLinks.map((link: any) => {
        const issue = link.linkedIssue;
        return {
          issueId: issue.issueId.toString(),
          issueName: issue.issueName,
          linkType: link.linkType,
          projectTitle: issueDetails.projectTitle,
          reporterName: issue.createdByUserName,
          issueCreatedAt: new Date(issue.createdAt),
          issueStatus: issue.issueStatus,
          issueTags:
            issue.issueTags?.map((tag: any) => ({
              tagId: tag.tagId,
              issueTagId: tag.issueTagId,
              tagName: `Tag ${tag.tagId}`, // change if tagName is available
            })) || [],
          assigneeName:
            issue.IssueAssignedToName ||
            issue.IssueAssignedByName ||
            "Unassigned",
          issueEndDate: issue.issueEndDate
            ? new Date(issue.issueEndDate)
            : null,
          severity: issue.priorityName || "None",
          issueLinkId: link.issueLinkId,
        };
      });
      setDataSource(linkedIssues);
    } catch (err) {
      console.error("Linked issues fetch failed", err);
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkIssue = async () => {
    if (!selectedIssue) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Please select an issue",
        confirmButtonText: "Ok",
      });
      return;
    }
    if (selectedIssue === issueDetails.issueId) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Link",
        text: "You cannot link an issue to itself.",
        confirmButtonText: "Ok",
      });
      return;
    }

    const existingLinks: any[] = issueDetails?.issueLinks || [];
    // An issue can only be linked once, regardless of link type — flag a
    // duplicate whenever the same issue is already linked.
    const isDuplicate =
      existingLinks.some(
        (link: any) =>
          String(link?.linkedIssueId ?? link?.linkedIssue?.issueId) ===
          String(selectedIssue)
      ) ||
      dataSource.some(
        (link) => String(link.issueId) === String(selectedIssue)
      );
    if (isDuplicate) {
      Swal.fire({
        icon: "warning",
        title: "Duplicate Link",
        text: "This issue is already linked.",
        confirmButtonText: "Ok",
      });
      return;
    }

    try {
      const payload = {
        projectId: issueDetails.projectId,
        issueId: issueDetails.issueId,
        issueLinks: {
          newLines: [
            {
              linkedIssueId: selectedIssue,
              linkType: linkType,
            },
          ],
        },
      };

      const response = await issueApis.updateIssuebyId(payload);
      if (response.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data?.message || "Issue linked successfully",
          confirmButtonText: "Ok",
        }).then(async () => {
          // Reset form and selection
          form.resetFields();
          setSelectedIssue(null);
          setLinkType("");

          // Refresh data
          await fetchLinkedIssues();
          await fetchIssueDropdown();

          // Notify parent component
          if (onIssueLinked) {
            onIssueLinked();
          }
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "Failed to link issue",
          confirmButtonText: "Ok",
        });
      }
    } catch (err) {
      console.error("Error linking issue", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to link issue",
        confirmButtonText: "Ok",
      });
    } finally {
      form.resetFields();
      setSelectedIssue(null);
      setLinkType("");
    }
  };

  const handleDeleteLink = async (issueLinkId: number) => {
    try {
      const payload = {
        projectId: issueDetails.projectId,
        issueId: issueDetails.issueId,
        issueLinks: {
          deletedLines: [{ issueLinkId }],
        },
      };

      const response = await issueApis.updateIssuebyId(payload);
      if (response.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.message,
          confirmButtonText: "Ok",
        }).then(async () => {
          // Refresh data immediately after delete
          await fetchLinkedIssues();
          await fetchIssueDropdown();

          // Notify parent component
          if (onIssueLinked) {
            onIssueLinked();
          }
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "Failed to delete issue link",
          confirmButtonText: "Ok",
        });
      }
    } catch (err) {
      console.error("Error deleting issue link", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete issue link",
        confirmButtonText: "Ok",
      });
    }
  };

  useEffect(() => {
    if (activeTab === "LinkIssue" && issueDetails?.issueLinks) {
      fetchLinkedIssues();
      fetchIssueDropdown();
    }
  }, [activeTab, issueDetails?.issueLinks]);

  const columns: ColumnType<LinkedIssueData>[] = [
    {
      title: "Issue Name",
      dataIndex: "issueName",
      key: "issueName",
      fixed: "left",
      width: 300,
      render: (text, record) => (
        <>
          <span
            // className="inlineTableCellLink"
            id={`issue-name-${record.issueId}`}
          >
            {record.issueName || "Demo Issue Name"}
          </span>
          <TooltipComponent
            iconClass="info-circle text-info"
            icon=""
            id={`issue-name-${record.issueId}`}
            direction="right"
            // text={`Click To View and Edit ${record.issueName} Details`}
            text={record.issueName}
            containerClassName="mt-1"
          />
        </>
      ),
    },
    {
      title: "Link Type",
      dataIndex: "linkType",
      key: "linkType",
      width: 130,
    },
    // {
    //   title: "Project",
    //   dataIndex: "projectTitle",
    //   key: "projectTitle",
    //   width: 180,
    //   render: (text: string) => <>{text}</>,
    // },
    {
      title: "Reporter",
      dataIndex: "reporterName",
      key: "reporterName",
      width: 150,
      render: (text) => (
        <span>
          {/* <Badge
            count={text?.[0] || "U"}
            style={{ backgroundColor: "#7265e6", marginRight: 6 }}
          /> */}
          {text}
        </span>
      ),
    },
    {
      title: "Created Time",
      dataIndex: "issueCreatedAt",
      key: "issueCreatedAt",
      width: 150,
      render: (date: Date) => dayjs(date).format("DD-MMM-YYYY HH:mm"),
    },
    {
      title: "Assignee",
      dataIndex: "assigneeName",
      key: "assigneeName",
      width: 150,
      render: (text) => (
        <span>
          {/* <Badge
            count={text === "Unassigned" ? "U" : text?.[0]}
            style={{ backgroundColor: "#777", marginRight: 6 }}
          /> */}
          {text}
        </span>
      ),
    },
    {
      title: "Tags",
      dataIndex: "issueTags",
      key: "issueTags",
      width: 150,
      render: (tags: IssueTagsInterface[], record, index) => {
        const tagText = tags.map((t) => t.tagName).join(", ");
        return (
          <>
            <span id={`issueTags-tooltip-${index}`}>
              {tagText.length > 25 ? `${tagText.slice(0, 22)}...` : tagText}
            </span>
            {tagText.length > 25 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`issueTags-tooltip-${index}`}
                direction="top"
                text={tagText}
                containerClassName=""
              />
            )}
          </>
        );
      },
    },
    {
      title: "Due Date",
      dataIndex: "issueEndDate",
      key: "issueEndDate",
      width: 150,
      render: (date: Date | null) =>
        date ? dayjs(date).format("DD-MMM-YYYY") : "--",
    },
    {
      title: "Status",
      dataIndex: "issueStatus",
      key: "issueStatus",
      width: 150,
      render: (status: string) => (status ? <span>{status}</span> : "--"),
    },
    {
      title: "Severity",
      dataIndex: "severity",
      key: "severity",
      width: 120,
      fixed: "right",
      render: (text) => text || "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        // <DeleteAccess moduleName="Issues">
        <Popconfirm
          title="Are you sure you want to delete this link?"
          onConfirm={() => handleDeleteLink(record.issueLinkId)}
          okText="Yes"
          cancelText="No"
          disabled={isFromWorkUI}
        >
          <Button
            type="text"
            icon={<DeleteOutlined style={{ color: "red" }} />}
            style={{ background: "transparent" }}
            disabled={isFromWorkUI}
          />
        </Popconfirm>
        // </DeleteAccess>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      {!isFromWorkUI && (
        <AddAccess moduleName="Issues">
          <Form layout="inline" style={{ marginBottom: 16 }}>
            <Form.Item label="Link Type">
              <Select
                style={{ width: 120 }}
                value={linkType}
                onChange={setLinkType}
                placeholder="Select type"
              >
                <Option value="Blocks">Blocks</Option>
                <Option value="DependsOn">Depends On</Option>
              </Select>
            </Form.Item>

            <Form.Item label="Issue" required>
              <Select
                style={{ width: 200 }}
                value={selectedIssue}
                onChange={setSelectedIssue}
                placeholder="Select issue"
                showSearch
              >
                {issueOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item>
              <Button type="primary" onClick={handleLinkIssue}>
                Link Issue
              </Button>
            </Form.Item>
          </Form>
        </AddAccess>
      )}

      <div className="card-inner card-inner-lg p-0">
        <div className="content clearfix">
          <Table
            rowKey="issueId"
            columns={columns}
            dataSource={dataSource}
            bordered
            size="middle"
            pagination={false}
            loading={loading}
            scroll={{ x: "max-content" }}
          />
        </div>
      </div>
    </div>
  );
};

export default LinkIssueTable;
